<template>
  <div>
    <Banner />

    <div class="container" v-if="studentPreferences">

      <ReviewModeInfo class="mb-4" />

      <CreatePlanSummary :studentType="formData.studentType" :studyOption="formData.studyOption" :teachingPeriod="formData.teachingPeriod"
        :startYear="formData.startYear" />

      <div class="row">
        <div class="col-12">
          <div v-if="reviewMode">
            <h2 class="h3">{{ __('label:review-preferences title') }}</h2>
          </div>
          <div v-else>
            <h2 class="h3 mb-2">{{ __('label:set-preference title') }}</h2>
            <p>Please answer a few questions to allow for the generation of a Course Plan.</p>
          </div>
        </div>
      </div>

      <div v-if="hasErrors && formErrors && reviewMode" class="row">
        <div class="col-12">
          <div class="page-alert warning mb-4">
            <i class="page-alert__icon far fa-exclamation-circle"></i>
            <div class="page-alert__body">
              <p>The following error(s) have been found with your previously selected preferences.</p>
              <ul class="mb-0">
                <li v-for="error in formErrors" :key="error">{{error[0]}}</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div class="row">
        <div class="col-6">
          <form>
            <InputSelect
              label="What best describes you?"
              id="studentType"
              :options="studentPreferences.options.studentTypes"
              :errors="getFieldValidation('studentType')"
              v-model="formData.studentType"
              v-on:input="updateForm"
              v-on:focusEmit="setHelpTip('studentType')"
              v-on:blurEmit="clearHelpTip" />

            <transition name="fade">
              <InputRadio v-if="formData.studentType == 'Transnational'"
                legend="Place of residence"
                radioGroupName="placeOfResidence"
                :options="studentPreferences.options.placesOfResidence"
                :errors="getFieldValidation('placeOfResidence')"
                v-model="formData.placeOfResidence"
                v-on:input="updateForm"
                invalidText="Please select a place of residence" />
            </transition>

            <InputSelect
              label="How would you like to study?"
              id="studyOption"
              :options="studentPreferences.options.studyOptions"
              :errors="getFieldValidation('studyOption')"
              v-model="formData.studyOption"
              v-on:input="updateForm"
              v-on:focusEmit="setHelpTip('studyOption')"
              v-on:blurEmit="clearHelpTip" />

            <transition name="fade">
              <div v-if="isInternationalPartTime" class="page-alert warning mb-4">
                <i class="page-alert__icon far fa-exclamation-circle"></i>
                <div class="page-alert__body">
                  In most circumstances international student visa holders need to study full time (4 units or 12 points per semester/trimester).
                </div>
              </div>
            </transition>

            <InputSelect v-if="formData.studentType != 'Transnational'"
              label="Have you already commenced studying at Murdoch?"
              id="existingStudent"
              :options="studentPreferences.options.existingStudents"
              :errors="getFieldValidation('existingStudent')"
              v-model="formData.existingStudent"
              v-on:input="updateForm"
              v-on:focusEmit="setHelpTip('existingStudent')"
              v-on:blurEmit="clearHelpTip" />

            <transition name="fade">

              <div v-if="formData.existingStudent == 'Yes' && formData.studentType != 'Transnational'">
                <div class="form-row d-flex align-items-end">
                  <div class="col-12">
                    <InputSelect
                      label="When did you commence study at Murdoch (for Fees purposes only)"
                      id="commencementYear"
                      defaultText="Select year"
                      :options="studentPreferences.options.commencementYears"
                      :errors="getFieldValidation('commencementYear')"
                      v-model="formData.commencementYear"
                      v-on:input="updateForm"
                      v-on:focusEmit="setHelpTip('commencementYear')"
                      v-on:blurEmit="clearHelpTip" />
                  </div>
                </div>
              </div>

            </transition>

            <div class="form-row d-flex align-items-end">
              <div class="col-5">
                <InputSelect
                  label="Date you plan to next enrol"
                  id="startYear"
                  defaultText="Select year"
                  :options="studentPreferences.options.startYears"
                  :errors="getFieldValidation('startYear')"
                  v-model="formData.startYear"
                  v-on:input="updateForm"
                  v-on:focusEmit="setHelpTip('startYear')"
                  v-on:blurEmit="clearHelpTip" />
              </div>
              <div class="col-7">
                <InputSelect
                  ariaLabelText="Start date - study period"
                  id="teachingPeriod"
                  defaultText="Select period"
                  :options="studentPreferences.options.teachingPeriods"
                  :errors="getFieldValidation('teachingPeriod')"
                  v-model="formData.teachingPeriod"
                  v-on:input="updateForm"
                  v-on:focusEmit="setHelpTip('teachingPeriod')"
                  v-on:blurEmit="clearHelpTip" />
              </div>
            </div>

            <transition name="fade">
              <div v-if="formData.teachingPeriod && formData.teachingPeriod.indexOf('Semester') > -1">
                <div class="form-row d-flex align-items-end">
                  <div class="col-12">
                    <InputSelect
                      label="Include scheduling units in Summer or Winter (Y/N)"
                      id="summerWinter"
                      defaultText="Select"
                      :options="studentPreferences.options.summerWinter"
                      :errors="getFieldValidation('summerWinter')"
                      v-model="formData.summerWinter"
                      v-on:input="updateForm"
                      v-on:focusEmit="setHelpTip('summerWinter')"
                      v-on:blurEmit="clearHelpTip" />
                  </div>
                </div>
              </div>
            </transition>

            <div class="form-actions">
              <button type="button" class="btn btn-secondary"
                @click="baseStep.previous">
                Back
              </button>
              <button type="button" class="btn btn-primary" v-bind:class="{disabled: !baseStep.valid()}"
                @click="setPreferences" :disabled="!baseStep.valid()">
                {{reviewMode ? 'Review units' : 'Select units'}}
              </button>
            </div>
          </form>
        </div>

        <div class="col-5 form-guide__outer">

          <transition name="fade">
            <div class="form-guide" v-if="inputHelpTip == 'studentType'">
              <div class="form-guide__section">
                <h3 class="form-guide__section-head">Domestic Student</h3>
                <div class="form-guide__section-content">You're considered a domestic student at Murdoch if you are an Australian citizen or permanent
                  resident,a
                  New Zealand citizen, or hold a permanent Australian humanitarian visa.
                </div>
              </div>
              <div class="form-guide__section">
                <h3 class="form-guide__section-head">International Student</h3>
                <div class="form-guide__section-content">You're an international student if you hold or are applying for a Student, Diplomatic, Bridging,
                  Temporary or Provisional Resident visa, or are a permanent resident of New Zealand.
                </div>
              </div>
              <div class="form-guide__section">
                <h3 class="form-guide__section-head">Transnational Student</h3>
                <div class="form-guide__section-content">You're a transnational student if you wish to study in Murdoch’s overseas location e.g. Dubai or
                  Singapore.
                </div>
              </div>
            </div>
          </transition>

          <transition name="fade">
            <div class="form-guide" v-if="inputHelpTip == 'studyOption'">
              <div class="form-guide__section">
                <h3 class="form-guide__section-head">Full Time or Part Time</h3>
                <div class="form-guide__section-content"><p>If you are on an international student visa, you will usually need to study full time
                  which equates to either 4 units or a total of 12 credit points per semester/trimester.</p>
                  <p>
                  Some courses require you to be enrolled full time, while other courses may only be available part time due to unit
                  availability and pre-requisite requirements.
                  </p>
                </div>
              </div>
            </div>
          </transition>

          <transition name="fade">
            <div class="form-guide" v-if="inputHelpTip == 'existingStudent'">
              <div class="form-guide__section">
                <h3 class="form-guide__section-head">Are you an existing Murdoch Student</h3>
                <div class="form-guide__section-content">If you are, please select 'Yes' here so we can help you plan effectively</div>
              </div>
            </div>
          </transition>

          <transition name="fade">
            <div class="form-guide" v-if="inputHelpTip == 'commencementYear'">
              <div class="form-guide__section">
                <h3 class="form-guide__section-head">When did you commence study</h3>
                <div class="form-guide__section-content">To help plan your course, we need to know when you started your studies at Murdoch</div>
              </div>
            </div>
          </transition>

          <transition name="fade">
            <div class="form-guide" v-if="inputHelpTip == 'startYear'">
              <div class="form-guide__section">
                <h3 class="form-guide__section-head">Date you plan to next enrol</h3>
                <div class="form-guide__section-content">This is the date you plan to next enrol in course units</div>
              </div>
            </div>
          </transition>

          <transition name="fade">
            <div class="form-guide" v-if="inputHelpTip == 'teachingPeriod'">
              <div class="form-guide__section">
                <h3 class="form-guide__section-head">Study Period</h3>
                <div class="form-guide__section-content">Study period refers to the Semester/Trimester that you wish to commence.
                </div>
              </div>
            </div>
          </transition>

          <transition name="fade">
            <div class="form-guide" v-if="inputHelpTip == 'summerWinter'">
              <div class="form-guide__section">
                <h3 class="form-guide__section-head">Study over Summer &amp;/or Winter</h3>
                <div class="form-guide__section-content">If you select yes, the course plan will include scheduling units in the Summer and/or Winter periods
                  if available.
                </div>
              </div>
            </div>
          </transition>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
</style>

<script>
import Vue from 'vue'
import useBaseStep from 'client/js/composition/base-step'
import InputSelect from './common/InputSelect.vue'
import InputRadio from './common/InputRadio.vue'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import StudentPreferences from 'client/js/models/student-preferences'
import Banner from './common/Banner.vue'
import ReviewModeInfo from './common/ReviewModeInfo.vue'
import errorModal from 'client/js/utils/error-modal'
import CreatePlanSummary from './CreatePlanSummary.vue'
import moment from 'moment'

export default {
  components: { InputSelect, InputRadio, Banner, CreatePlanSummary, ReviewModeInfo },
  setup(props, context) {
    return {
      baseStep: useBaseStep(props, context)
    }
  },
  data() {
    return {
      loading: false,
      inputHelpTip: null,
      formData: {},
      formErrors: {},
      studentPreferences: null, // Will hold Student Preferences instantiated object once initialised with the course
      isValidStartDate: null,
      isInternationalPartTime: null
    }
  },
  computed: {
    ...mapGetters({
      course: 'courses/getSelectedCourse',
      savedPlan: 'savedCourse/getSavedCoursePlan',
      selectedPreferences: 'preferences/getSelectedStudyPreferences',
      validationStates: 'savedCourse/getValidationStates'
    }),
    reviewMode: function () {
      return this.savedPlan != null
    },
    hasErrors: function () {
      return !this.validationStates.preferencesValid
    }
  },
  methods: {
    ...mapMutations({
      setStudyPreferences: 'preferences/setStudyPreferences',
      resetPlan: 'plans/resetState'
    }),
    ...mapActions({
      populateTeachingPeriods: 'preferences/populateTeachingPeriods'
    }),
    updateForm(inputName, input) {
      Vue.set(this.formData, `${inputName}`, input)
      this.validateForm()
    },
    setHelpTip(selectedTip) {
      this.inputHelpTip = selectedTip
    },
    clearHelpTip() {
      this.inputHelpTip = null
    },
    validateForm: async function () {
      try {
        await this.studentPreferences.setData(this.formData)
        this.formErrors = await this.studentPreferences.validate()
        this.baseStep.valid(this.studentPreferences.isValid)
        this.isInternationalPartTime = this.formData.studentType == 'International' && this.formData.studyOption && this.formData.studyOption != 'Full Time - 12'
      } catch (err) {
        await errorModal(this, 'preferences-error', err)
        this.$router.go()
      }
    },
    setPreferences: async function () {
      if (JSON.stringify(Object.values(this.formData)) !== JSON.stringify(Object.values(this.selectedPreferences))) {
        this.resetPlan()
      }
      if (this.baseStep.valid()) {
        let data = this.formData
        this.setStudyPreferences(data)
        if (this.$gtm.enabled())
          this.$gtm.trackEvent({
            event: 'site interaction',
            category: 'Select Preferences',
            action: 'click',
            label: 'Preferences Selected',
            value: data,
            rest: {
              course: {
                code: this.course.code,
                name: this.course.name
              },
              preferences: data
            }
          })

        //additionally select out the teaching periods, so we can use them durign the unit selection, and course plan
        try {
          await this.populateTeachingPeriods()
        } catch (err) {
          await errorModal(this, 'server-error', err)
          this.$router.go()
        }
        this.baseStep.next()
      }
    },
    getFieldValidation(inputName) {
      return this.formErrors ? this.formErrors[inputName] || [] : []
    }
  },
  created() {},
  watch: {},
  mounted: async function () {
    this.studentPreferences = new StudentPreferences()
    await this.studentPreferences.setCourse(this.course)
    if (this.selectedPreferences) {
      Object.assign(this.formData, this.selectedPreferences)
      await this.validateForm()
    }
  },
  destroyed() {}
}
</script>

<style>
</style>
